import { BigModal, TextDefault } from '@awarego-ui/components';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import ReportFormFooter from '../forms/ReportFormFooter';
import ReportRecurringForm from '../forms/recurring/ReportRecurringForm';
import { useStores } from '../../../hooks/use-stores';
import ThemedSpinner from '../../../components/themed/ThemedSpinner';

function ReportRecurringModal({
    isOpen,
    onRequestClose,
    reportsForm,
    lists,
    usersTimeZone,
    companyId,
    reportId, //If supplied, we fetch a report with that ID to use to pre-fill the form
    editingReport, //If supplied, used to pre-fill the form
}) {
    const { reportsStore, brandingStore } = useStores();
    const isEditing = !!(editingReport || reportId);
    const tagsFeatureEnabled = brandingStore.reportsTagsEnabled;
    const reportType = reportsForm.fields?.reportType?.value || '';
    const isDepartmentManager = reportType === 'department-manager';
    const needsTags = tagsFeatureEnabled || isDepartmentManager;

    useEffect(() => {
        const fetchData = async () => {
            await reportsStore.loadSingleReport(companyId, reportId);
        };
        if (reportId) {
            fetchData().catch(console.error);
        }
    }, [companyId, reportId, reportsStore]);

    useEffect(() => {
        if (!companyId) return;
        if (!needsTags) return;
        if (reportsStore.reportTags.length > 0) return;
        reportsStore.loadReportTags(companyId);
    }, [companyId, reportsStore, reportsStore.reportTags.length, needsTags]);

    useEffect(() => {
        if (!needsTags) return;
        if (!isOpen) return;
        try {
            const tagField = reportsForm.$('reportTags');
            if (tagField) {
                const existingExtra = Array.isArray(tagField.extra)
                    ? tagField.extra
                    : [];
                const merged = Array.from(
                    new Set([...existingExtra, ...reportsStore.reportTags])
                ).sort((a, b) =>
                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                );
                tagField.set('extra', merged);
            }
        } catch (err) {
            // reportTags field may not exist in some form variants
        }
    }, [isOpen, reportsForm, reportsStore.reportTags, needsTags]);

    useEffect(() => {
        if (isOpen) {
            reportsStore.setError(null);
        }
    }, [isOpen, reportsStore]);

    const handleCancel = () => {
        reportsStore.setError(null);
        onRequestClose();
    };

    return (
        <BigModal
            shouldCloseOnOverlayClick={false}
            isOpen={isOpen}
            onRequestClose={handleCancel}
            heading={'Schedule report' + (isEditing ? ' (edit)' : '')}
            modalContent={
                reportId && reportsStore.loadingReport ? (
                    <ThemedSpinner />
                ) : (
                    <>
                        {reportsStore.error && (
                            <TextDefault
                                mb={16}
                                style={{ color: 'var(--color-error, #d32f2f)' }}
                            >
                                {reportsStore.error}
                            </TextDefault>
                        )}
                        <ReportRecurringForm
                            reportsForm={reportsForm}
                            lists={lists}
                            usersTimeZone={usersTimeZone}
                            editingReport={
                                editingReport ||
                                (reportId && reportsStore.report)
                            }
                            enableTags={needsTags}
                            companyId={companyId}
                        />
                    </>
                )
            }
            modalFooterContent={
                <ReportFormFooter
                    reportsForm={reportsForm}
                    submitText={'Save'}
                    creatingReport={reportsStore.addingReport}
                    onCancel={handleCancel}
                    onSubmit={() => {
                        reportsForm.submit(isEditing);
                    }}
                />
            }
        />
    );
}

export default observer(ReportRecurringModal);
